import * as React from 'react';
import {
  CapApiService,
  CompleteManifestUpdate,
  ManifestSource,
  ReportUriContainer,
  useCapApiService,
} from '@cengage/cap-portal-api-lib-react';
import {
  CapAuthenticationService,
  TOKEN_PARAMETER,
  useCapAuthenticationService,
} from '@cengage/cap-portal-authentication-lib-react';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { CafeServiceInterface } from '@cafe/core';
import md5 from 'crypto-js/md5';
import hex from 'crypto-js/enc-hex';
import { useCafe } from '@cafe/react';
import { SubscribingEntity } from '../shared/subscribing-entity';

export enum ReportState {
  Requested = 'Requested',
  Pending = 'Pending',
  Ready = 'Ready',
}

export enum ManifestState {
  Initial = 'Initial',
  Loaded = 'Loaded',
  Synthetic = 'Synthetic',
}

export interface LocalManifestReport extends ReportUriContainer {
  reportDisplayName: string;
  generationDate?: string;
  expireOnDate?: string;
  reportTypeDescription: string;
  reportTypeUri: string;
  state: ReportState;
  id: string;
}

export interface LocalManifest {
  manifestState: ManifestState;
  reports: LocalManifestReport[];
}

export interface LocalCompleteManifestUpdate
  extends CompleteManifestUpdate<LocalManifestReport> {}

const ReportStateMap = {
  ready: ReportState.Ready,
  pending: ReportState.Pending,
};

export class ManifestService extends SubscribingEntity {
  private readonly cachedReports: LocalManifest = {
    manifestState: ManifestState.Initial,
    reports: [],
  };

  private readonly subject: BehaviorSubject<LocalManifest> =
    new BehaviorSubject(this.cachedReports);
  private readonly errorSubject: Subject<any> = new Subject();
  private manifestSubscription!: Subscription;

  constructor(
    private apiService: CapApiService,
    private authenticationService: CapAuthenticationService,
    private eventingService: CafeServiceInterface
  ) {
    super();
    this.ensureManifestIsLoaded();
  }

  private ensureManifestIsLoaded(): void {
    this.addSubscriptionToCloseOnDestroy(
      this.apiService.subscribeToManifestErrors().subscribe((manifestError) => {
        this.errorSubject.next(manifestError);
      })
    );
    this.addSubscriptionToCloseOnDestroy(
      this.authenticationService
        .subscribeToAuthenticationNotifications()
        .subscribe((isAuthenticated) => {
          if (isAuthenticated) {
            this.apiService.connect(
              this.authenticationService.getToken()!,
              this.authenticationService.getUserIdentifier()!,
              true
            );
            this.manifestSubscription = this.apiService
              .subscribeToCompleteManifestUpdates<
                LocalManifestReport,
                LocalCompleteManifestUpdate
              >(
                {
                  source: ManifestSource.Initial,
                  update: this.cachedReports.reports,
                },
                (report) => {
                  if (report.displayInPortal) {
                    return {
                      reportUri: report.reportUri,
                      reportDisplayName: report.reportDisplayName,
                      generationDate: report.generationDate,
                      expireOnDate: report.expireOnDate,
                      state: ReportStateMap[report.state],
                      reportTypeDescription: report.reportTypeDisplayName,
                      reportTypeUri: report.reportType,
                      id: hex.stringify(md5(report.reportUri)),
                    } as LocalManifestReport;
                  }
                  return null!;
                }
              )
              .subscribe({
                next: (message) => {
                  if (message && message.source !== ManifestSource.Initial) {
                    this.cachedReports.manifestState = ManifestState.Loaded;
                    this.cachedReports.reports = message.update;
                    this.subject.next(this.cachedReports);
                  } else if (
                    message &&
                    message.source === ManifestSource.Initial
                  ) {
                    this.cachedReports.manifestState = ManifestState.Initial;
                  }
                },
              });
            this.addSubscriptionToCloseOnDestroy(this.manifestSubscription);
          } else {
            if (this.manifestSubscription) {
              this.manifestSubscription.unsubscribe();
              this.manifestSubscription = null!;
            }
          }
        })
    );
  }

  getReportManifest(): Observable<LocalManifest> {
    return this.subject.asObservable();
  }

  getManifestErrors(): Observable<any> {
    return this.errorSubject.asObservable();
  }

  addRequestedManifestEntry(
    reportUri: string,
    reportDisplayName: string,
    reportTypeDescription: string,
    reportTypeUri: string
  ): void {
    if (
      this.cachedReports.reports.findIndex((r) => r.reportUri === reportUri) ===
      -1
    ) {
      this.cachedReports.reports.push({
        reportUri,
        reportDisplayName,
        state: ReportState.Requested,
        reportTypeDescription,
        reportTypeUri,
        id: hex.stringify(md5(reportUri)),
      });
      if (this.cachedReports.manifestState === ManifestState.Initial) {
        this.cachedReports.manifestState = ManifestState.Synthetic;
      }
      this.subject.next(this.cachedReports);
    }
  }

  getReport(report: LocalManifestReport): boolean {
    window.open(
      this.apiService.getReportRedirectUrl(
        report.reportUri,
        TOKEN_PARAMETER,
        this.authenticationService.getToken()!
      ),
      '_blank'
    );
    this.eventingService.recordActivity({
      eventCategory: 'GET_REPORT',
      eventAction: 'GET_REPORT',
      tags: {
        reportType: report.reportTypeUri,
        reportUri: report.reportUri,
      },
    });
    return false;
  }
}

export const MANIFEST_SERVICE = React.createContext<ManifestService>(null!);

export function ManifestServiceProvider({ children }: React.PropsWithChildren) {
  const apiService = useCapApiService();
  const authenticationService = useCapAuthenticationService();
  const eventingService = useCafe();
  const [manifestService] = React.useState(
    () =>
      new ManifestService(apiService, authenticationService, eventingService)
  );

  React.useEffect(() => {
    return () => {
      manifestService.onDestroy();
    };
  }, [manifestService]);

  return (
    <MANIFEST_SERVICE.Provider value={manifestService}>
      {children}
    </MANIFEST_SERVICE.Provider>
  );
}

export function useManifestService() {
  return React.useContext(MANIFEST_SERVICE);
}
